<template>
  <v-container class="pa-0" fluid>

    <v-fade-transition hide-on-leave>
      <template v-if="loading">
        <v-row align="center" class="fill-height" justify="center">
          <v-progress-circular size="64" indeterminate color="primary"/>
        </v-row>
      </template>
    </v-fade-transition>

    <template v-if="!loading">

      <v-row no-gutters justify="center" :align="$vuetify.breakpoint.mdAndUp ? 'start' : 'start'" class="">
        <v-col cols="12" md="10" lg="8">
          <div class="px-12 pb-5">
            <h1 class="text-h5 text-sm-h1 mt-3 mb-6">{{ $t('tassu.title') }}</h1>
            <p>{{ $t('tassu.paragraph1') }}</p>
            <p>{{ $t('tassu.paragraph2') }}</p>
            <p>
              {{ $t('tassu.paragraph3_before') }}
              <a :href="$t('tassu.paragraph3_link')" target="_blank" rel="noopener noreferrer">{{ $t('tassu.paragraph3_link') }}</a>
              {{ $t('tassu.paragraph3_after') }}
            </p>
            <p>{{ $t('tassu.paragraph4') }}</p>
            <p>
              <a :href="$t('tassu.feedback_link')" target="_blank" rel="noopener noreferrer">{{ $t('tassu.feedback_text') }}</a>
            </p>
          </div>
        </v-col>
        <v-col cols="12" md="10" lg="8">
          <v-stepper v-model="currentStep" flat vertical class="wizard-stepper">

                <v-stepper-step :complete="currentStep > 1" step="1">
                  {{ $t('wizard.study_program') }}
                </v-stepper-step>
                <v-stepper-content step="1" class="ml-0">
                  <v-card outlined color="stepperBackground">
                    <v-card-title class="px-6 text-h5 text-sm-h1 text-break">
                      {{ $t('wizard.study_program') }}
                    </v-card-title>
                    <v-card-subtitle class="body-1 text--black mb-5 px-6">
                      {{ $t('wizard.start_by_selecting_study_program') }}
                    </v-card-subtitle>
                    <v-card-text class="px-6">
                      <v-combobox
                        v-model="selectedProgram"
                        :items="studyPrograms"
                        item-text="name"
                        item-value="code"
                        :filter="filterStudyProgram"
                        :label="$t('wizard.select_study_program')"
                        solo
                        flat
                        color="white"
                        hide-details
                        return-object
                      >
                        <template v-slot:item="{item}">
                          <v-list-item-content>
                            <v-list-item-title class="text-no-wrap">{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle class="grey--text text--darken-2">{{ item.code }}</v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-combobox>
                    </v-card-text>
                    <v-card-actions class="px-6 pb-4">
                      <v-spacer></v-spacer>
                      <v-btn
                        color="black"
                        dark
                        :x-large="$vuetify.breakpoint.smAndUp"
                        depressed
                        @click="nextStep"
                        :disabled="!selectedProgram"
                      >
                        {{ $t('wizard.continue') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>

                <v-stepper-step :complete="currentStep > 2" step="2">
                  {{ $t('wizard.your_skills') }}
                </v-stepper-step>
                <v-stepper-content step="2" class="ml-0">
                  <v-card outlined color="stepperBackground">
                    <v-card-title class="px-6 text-h5 text-sm-h1 text-break">
                      {{ $t('wizard.your_skills') }}
                    </v-card-title>
                    <v-card-text class="px-6">
                      <p class="body-1 text--black">{{ $t('wizard.tell_us_about_your_general_competences') }}</p>
                      <v-textarea
                        v-model="generalCompetences"
                        solo
                        flat
                        :disabled="loadingNextStep"
                        hide-details
                        :label="$t('wizard.enter_your_general_competences')"
                        rows="5"
                        class="mb-6"
                      ></v-textarea>
                      <p class="body-1 text--black">{{ $t('wizard.tell_us_about_your_skills') }}</p>
                      <v-textarea
                        v-model="skills"
                        solo
                        flat
                        :disabled="loadingNextStep"
                        hide-details
                        :label="$t('wizard.enter_your_skills')"
                        rows="5"
                      ></v-textarea>
                    </v-card-text>
                    <v-card-actions class="px-6 pb-4">
                      <v-spacer></v-spacer>
                      <v-btn text
                             :x-large="$vuetify.breakpoint.smAndUp"
                             color="black"
                             :disabled="loadingNextStep"
                             @click="prevStep">
                        {{ $t('wizard.back') }}
                      </v-btn>
                      <v-btn
                        color="black"
                        dark
                        :x-large="$vuetify.breakpoint.smAndUp"
                        depressed
                        :loading="loadingNextStep"
                        @click="submitSkills"
                      >
                        {{ $t('wizard.continue') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>

                <v-stepper-step :complete="currentStep > 3" step="3">
                  {{ $t('wizard.prompt') }}
                </v-stepper-step>
                <v-stepper-content step="3" class="ml-0">
                  <v-card outlined color="stepperBackground">
                    <v-card-title class="px-6 text-h5 text-sm-h1 text-break">
                      {{ $t('wizard.prompt') }}
                    </v-card-title>
                    <v-card-subtitle class="body-1 text--black mb-5 px-6">
                      {{ $t('wizard.confirm_prompt') }}<br /><br />
                      {{ $t('wizard.confirm_prompt_2') }}
                    </v-card-subtitle>
                    <v-card-text class="px-6">
                      <v-textarea
                        v-model="promptSuggestion"
                        solo
                        flat
                        hide-details
                        :disabled="loadingNextStep"
                        :label="$t('wizard.prompt_suggestion')"
                        rows="5"
                      ></v-textarea>
                    </v-card-text>
                    <v-card-actions class="px-6 pb-4">
                      <v-spacer></v-spacer>
                      <v-btn text
                             :x-large="$vuetify.breakpoint.smAndUp"
                             color="black"
                             :disabled="loadingNextStep"
                             @click="resetWizard">
                        {{ $t('wizard.new_search') }}
                      </v-btn>
                      <v-btn
                        color="black"
                        dark
                        :x-large="$vuetify.breakpoint.smAndUp"
                        depressed
                        :loading="loadingNextStep"
                        @click="submitPrompt"
                      >
                        {{ $t('wizard.continue') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-stepper-content>

            <v-stepper-step step="4">
              {{ $t('wizard.suggested_studies') }}
            </v-stepper-step>
            <v-stepper-content step="4" class="ml-0">
              <v-card outlined color="stepperBackground">
                <v-card-title class="px-6 text-h5 text-sm-h1 text-break">
                  {{ $t('wizard.suggested_studies') }}
                </v-card-title>
                <v-card-subtitle class="body-1 text--black px-6"
                                 v-html="$t('wizard.suggested_studies_help', {link1: 'https://samkstudyguide.solenovo.fi/home', link2: 'https://opintohaku.samk.fi/'})">
                </v-card-subtitle>
                <v-card-text class="body-1 text--black px-6 mt-4 mb-0 pb-0" v-if="selectedProgram">
                  <v-alert text class="mb-0">
                    <strong>{{ $t('wizard.selected_study_program') }}:</strong>
                    <br>{{ selectedProgram.code }} {{ selectedProgram.name }}
                  </v-alert>
                </v-card-text>
                <v-card-text class="px-6">
                  <marked-wrapper :content="suggestedStudies"></marked-wrapper>
                </v-card-text>
                <v-card-text class="body-1 text--black px-6 mb-0 pb-0" v-if="suggestedStudies">
                  <p>{{ $t('wizard.suggested_studies_help_2') }}</p>
                  <p>{{ $t('wizard.suggested_studies_help_3') }}</p>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                  <v-spacer></v-spacer>
                  <v-btn text
                         :x-large="$vuetify.breakpoint.smAndUp"
                         color="black"
                         :disabled="loadingNextStep"
                         @click="resetWizard">
                    {{ $t('wizard.new_search') }}
                  </v-btn>
                  <v-btn
                    color="black"
                    dark
                    :x-large="$vuetify.breakpoint.smAndUp"
                    depressed
                    :loading="generatingPdf"
                    @click="generatePdf">
                    {{ $t('wizard.print') }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>
          </v-stepper>
        </v-col>
      </v-row>
    </template>

  </v-container>
</template>

<script>
import chatApi from "@/api/chat-api";
import axios from "axios";
import store from "@/store";
import MarkedWrapper from "@/views/Wizard/components/MarkedWrapper.vue";
import { jsPDF } from "jspdf";

function resolveEnv() {
  try {
    return window.parent.location === window.location ? 'standalone' : 'embedded';
  } catch (error) {
    console.log(error);
    return 'embedded_err';
  }
}

export default {
  name: "WizardView",
  components: {MarkedWrapper},
  data() {
    return {
      loading: true,
      loadingNextStep: false,
      generatingPdf: false,
      activeChat: null,
      chatId: null,
      studyPrograms: [],
      selectedProgram: null,
      generalCompetences: "",
      skills: "",
      skillsResponse: {},
      promptSuggestion: "",
      currentStep: 1,
      suggestedStudies: "",
    };
  },
  methods: {
    filterStudyProgram(item, queryText) {
      const q = (queryText || '').toLowerCase();
      const name = (item.name || '').toLowerCase();
      const code = (item.code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    },
    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep = this.currentStep + 1;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep = this.currentStep - 1;
      }
    },
    resetWizard() {
      this.currentStep = 2;
      this.generalCompetences = '';
      this.skills = '';
      this.promptSuggestion = '';
      this.suggestedStudies = '';
      this.skillsResponse = null;
      this.chatId = null;
    },
    async generatePdf() {
      try {
        this.generatingPdf = true;

        // Create a new PDF document
        const doc = new jsPDF();

        // Set the font to match the UI
        doc.setFont("helvetica", "normal");

        // Add the logo to the top right corner
        const logoImg = new Image();
        logoImg.src = '/img/samk_logo.png';

        // Wait for the image to load
        await new Promise((resolve) => {
          logoImg.onload = resolve;
        });

        const tassuImg = new Image();
        tassuImg.src = '/img/tassu800x800.png';
        await new Promise((resolve) => {
          tassuImg.onload = resolve;
        });

        // Add the logo to the PDF (positioned at the top right)
        doc.addImage(logoImg, 'png', 150, 10, 40, 40);
        doc.addImage(tassuImg, 'png', 115, 16, 27, 27);

        // Add study program information
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(this.$t('wizard.selected_study_program'), 20, 45);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.text(this.selectedProgram.code + ' ' + this.selectedProgram.name, 20, 55);

        // Add general competences section
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(this.$t('wizard.your_general_competences'), 20, 70);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);

        const generalLines = doc.splitTextToSize(this.generalCompetences, 170);
        doc.text(generalLines, 20, 80);

        // Add skills section
        const skillsHeadingY = 90 + (generalLines.length * 7);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(this.$t('wizard.your_skills'), 20, skillsHeadingY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);

        // Split skills text into lines to fit the page width
        const skillsLines = doc.splitTextToSize(this.skills, 170);
        doc.text(skillsLines, 20, skillsHeadingY + 10);

        // Add prompt section
        const promptY = skillsHeadingY + 20 + (skillsLines.length * 7);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text(this.$t('wizard.prompt'), 20, promptY);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);

        // Split prompt text into lines to fit the page width
        const promptLines = doc.splitTextToSize(this.promptSuggestion, 170);
        doc.text(promptLines, 20, promptY + 10);

        // Add suggested studies section
        const suggestedStudiesY = promptY + 10 + (promptLines.length * 7);

        // Convert markdown to plain text for the PDF
        let plainText = this.suggestedStudies.replace(/#+\s/g, ''); // Remove markdown headings
        plainText = plainText.replace(/\*\*/g, ''); // Remove bold markers
        plainText = plainText.replace(/\*/g, ''); // Remove italic markers

        // Split suggested studies text into lines to fit the page width
        const suggestedStudiesLines = doc.splitTextToSize(plainText, 170);

        // Check if we need a new page for suggested studies
        let helpTextY;
        if (suggestedStudiesY + 10 + (suggestedStudiesLines.length * 7) > 280) {
          doc.addPage();
          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.text(this.$t('wizard.suggested_studies'), 20, 20);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(12);
          doc.text(suggestedStudiesLines, 20, 30);
          helpTextY = 30 + (suggestedStudiesLines.length * 7) + 10;
        } else {
          doc.text(suggestedStudiesLines, 20, suggestedStudiesY + 10);
          helpTextY = suggestedStudiesY + 10 + (suggestedStudiesLines.length * 7) + 10;
        }

        // Add help text at the end
        const helpText = this.$t('wizard.suggested_studies_help_3_print');
        const helpTextLines = doc.splitTextToSize(helpText, 170);
        if (helpTextY + (helpTextLines.length * 7) > 280) {
          doc.addPage();
          helpTextY = 20;
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.text(helpTextLines, 20, helpTextY);

        // Save the PDF
        doc.save(this.$t('wizard.pdf_filename'));
      } catch (error) {
        console.error('Error generating PDF:', error);
      } finally {
        this.generatingPdf = false;
      }
    },
    async submitSkills() {
      try {
        this.loadingNextStep = true;
        await this.createNewChat();
        const combined =
          `Yleiset osaamiset:\n${this.generalCompetences}\n\n` +
          `Työ-/harrastustehtävät ja vastuut:\n${this.skills}`;
        const response = await chatApi.getCompetencePrompt({chatId: this.chatId, work_history: combined});
        this.skillsResponse = response;
        this.promptSuggestion = response.reply;
        this.nextStep();
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingNextStep = false;
      }
    },
    async submitPrompt() {
      try {
        this.loadingNextStep = true;
        await this.streamResponse();
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingNextStep = false;
      }
    },
    async createNewChat() {
      try {
        const env = resolveEnv();
        const chat = await chatApi.newChat({name: "Opinnollistaminen", environment: env});
        this.chatId = chat.id;
        this.activeChat = chat.id;
      } catch (error) {
        alert(error);
      }
    },
    async streamResponse() {
      try {
        const response = await fetch(`/api/chat/${this.chatId}/message-stream`, {
          method: 'POST',
          body: JSON.stringify({
            chatId: this.chatId,
            competence: this.promptSuggestion,
            ops_code: this.selectedProgram.code,
            timestamp: new Date().getTime(),
            indexName: 'fi',
          }),
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'x-api-key': axios.defaults.headers.common['x-api-key'],
            'x-lang': this.$i18n.locale,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let accumulatedResponse = '';

        try {
          // Read the stream
          const processStream = async () => {
            try {
              const { value, done } = await reader.read();
              if (done) {
                return;
              }

              const text = decoder.decode(value);
              accumulatedResponse += text;
              this.suggestedStudies = accumulatedResponse;

              if (this.currentStep < 4) {
                this.nextStep();
              }

              await processStream();
            } catch (error) {
              console.error('Error reading stream:', error);
            }
          };

          await processStream();
        } finally {
          await reader.cancel();
        }
      } catch (error) {
        console.error('streamResponse error:', error);
        throw error;
      }
    }

  },
  async mounted() {
    axios.defaults.headers.common['x-api-key'] = store.state.sessionId;
    try {
      this.studyPrograms = await chatApi.getStudyPrograms();
    } catch (e) {
      console.error('Failed to load study programs:', e);
      this.studyPrograms = [];
    }
    this.loading = false;
  }
}
</script>

<style scoped lang="scss">
.wizard-stepper {

  .v-stepper__content {
    position: relative;
    border-left: 0 !important;

    &:not(:last-child) {
      &:before {
        content: "";
        border-left: 1px solid #e8e8e8;
        position: absolute;
        left: 35px;
        top: 0;
        bottom: 0;
        width: 1px;
      }
    }
  }
}
</style>
